import { Location, Order } from "@/types/tms";

/**
 * Compute a display name for a location from its customer + short_code.
 * e.g. "Aurora - ELP1" or just "ELP1" if no customer.
 */
export function getLocationDisplayName(location: {
  short_code?: string;
  customer?: { organization_name: string } | null;
}): string {
  const customer = location.customer?.organization_name || "";
  const code = location.short_code || "";
  return customer ? `${customer} - ${code}` : code;
}

/**
 * Normalize city name for comparison (lowercase, trimmed)
 */
export function normalizeCity(city: string | null | undefined): string {
  return (city || "").toLowerCase().trim();
}

/**
 * Check if two time ranges overlap
 */
export function doTimesOverlap(
  start1: Date,
  end1: Date,
  start2: Date,
  end2: Date
): boolean {
  return start1 < end2 && end1 > start2;
}

/**
 * Get resource location at a specific time based on its orders.
 * Returns ship_to_city of the most recent completed order, or home base city if none.
 * 
 * @param homeBaseCity - The home base city to fall back to
 * @param orders - Orders sorted by ready_time ascending
 * @param atTime - The time to check location at
 * @returns The city name where the resource is located
 */
export function getResourceLocationAtTime<T extends { ready_time: string; requested_delivery_time: string; ship_to_city: string }>(
  homeBaseCity: string | null,
  orders: T[],
  atTime: Date
): string | null {
  // Find the last order that ended before atTime by iterating backwards
  for (let i = orders.length - 1; i >= 0; i--) {
    const orderEnd = new Date(orders[i].requested_delivery_time);
    if (orderEnd <= atTime) {
      return orders[i].ship_to_city;
    }
  }
  // No completed orders before atTime - resource is at home base
  return homeBaseCity;
}

/**
 * Get stale order IDs (orders older than specified days)
 */
export function getStaleOrderIds<T extends { id: string; requested_delivery_time: string }>(
  orders: T[],
  daysOld: number = 7
): Set<string> {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - daysOld);
  
  const staleIds = new Set<string>();
  for (const order of orders) {
    if (new Date(order.requested_delivery_time) < cutoff) {
      staleIds.add(order.id);
    }
  }
  return staleIds;
}

/**
 * Find the saved location whose street address and city exactly match a
 * form/CSV stop. Exact, case-sensitive match on purpose: callers use the
 * result for timezone conversion and EAPI trip bodies, so a near-miss must
 * resolve to "no location" rather than the wrong one.
 */
export function findLocationByAddress(
  locations: Location[] | undefined,
  street: string,
  city: string,
): Location | null {
  if (!locations) return null;
  return (
    locations.find(
      (l) => l.street_address === street && l.city === city,
    ) ?? null
  );
}
